 
import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import { FaEye } from "react-icons/fa";
import { Files } from "lucide-react";

import { Stack, IconButton, Button, Box, useMediaQuery } from '@mui/material';
import {
  Dialog,
  DialogTitle,
  DialogContent
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TableEmptyRow from '../shared/TableEmptyRow';
import axios from 'axios';

const Table = ({
  examType,
  reports,
  loading,
  page,
  setPage,
  rowsPerPage,
  setPdfUrl,
  setPdfDialogOpen,
  totalCount,
  pendingDialogOpen,
  examId,
  setPendingDialogOpen,
  fetchAllFilteredReports,
  attemptFilter
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [pendingSearch, setPendingSearch] = useState("");
  const [pendingReports, setPendingReports] = useState([]);
  const [pendingTotalCount, setPendingTotalCount] = useState(0);
  const [pendingPage, setPendingPage] = useState(1);
  const pendingRowsPerPage = 10;
  // const pendingTotalPages = Math.ceil(pendingTotalCount / pendingRowsPerPage);
 
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [selectedReports, setSelectedReports] = useState([]);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [questionVideos, setQuestionVideos] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
 
  const filteredReports = reports.filter((row) => {
    const status = String(row.status || "").trim().toLowerCase();
    return ["passed", "failed"].includes(status);
  });
  const isCompetencyExam = examType === "Competency Review";
  const allFilteredReportsSelected = totalCount > 0 && selectedReports.length >= totalCount;

  // Fetch ALL reports (used only when no selection is made)
  const fetchAllReports = async () => {
    try {
      const allReports = fetchAllFilteredReports
        ? await fetchAllFilteredReports()
        : [];

      return allReports.filter((row) => {
        const status = String(row.status || "").trim().toLowerCase();
        return ["passed", "failed"].includes(status);
      });
    } catch (error) {
      console.error("Fetch all reports error:", error);
      return [];
    }
  };

  const fetchPendingReports = async (pageNum = 1, searchValue = pendingSearch) => {
    try {
      const params = new URLSearchParams({
        exam_id: examId,
        search: searchValue,
        page: pageNum,
        page_size: pendingRowsPerPage,
      });
      params.append('status', 'Pending');
      params.append('status', 'Missed');
      params.append('status', 'Started');

      const url = `/reports/employees/?${params.toString()}`;
      const response = await axios.get(url, { withCredentials: true });
      const data = response.data;

      setPendingReports(data.reports || []);
      setPendingTotalCount(data.total_count || 0);
      setPendingPage(pageNum);
    } catch (error) {
      console.error("Pending reports fetch error:", error);
      setPendingReports([]);
      setPendingTotalCount(0);
    }
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const pendingTotalPages = Math.ceil(
    pendingTotalCount / pendingRowsPerPage
  );

  const renderPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (page <= 3) {
      pages.push(1, 2, 3, '...', totalPages);
    } else if (page >= totalPages - 2) {
      pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
    }
    return pages;
  };

  const handleViewReport = async (row) => {
    try {
      const examTypeId = examType === "Competency Review" ? 1 : 2;

      const response = await axios.get(`/reports/exam-report/${row.ee_guid}/${examTypeId}/`, {
        withCredentials: true
      });
      const data = response.data;

      if (response.status < 200 || response.status >= 300) {
        alert(data.error || "Failed to fetch report");
        return;
      }

      const generateResponse = await axios.post(
        `/reports/exam-report/${row.ee_guid}/${examTypeId}/`,
        {},
        { withCredentials: true }
      );

      const pdfData = generateResponse.data;

      if (!pdfData.success) {
        alert(pdfData.error || "Failed to generate PDF");
        return;
      }

      const byteCharacters = atob(pdfData.fileBase64);
      const byteNumbers = [...byteCharacters].map(c => c.charCodeAt(0));

      const blob = new Blob([new Uint8Array(byteNumbers)], { type: "application/pdf" });

      setPdfUrl(URL.createObjectURL(blob));
      setPdfDialogOpen(true);
    } catch (error) {
      console.error("Error fetching report:", error);
    }
  };

  // ================== UPDATED: CSV Export (respects selection) ==================
  const handleExportCSV = async () => {
    let reportsToExport = [];

    if (selectedReports.length > 0) {
      reportsToExport = selectedReports;
    } else {
      reportsToExport = await fetchAllReports();
    }

    if (reportsToExport.length === 0) {
      alert("No data to export!");
      return;
    }

    const headers = ["S.No", "Name", "Employee ID", "Attempt Count", "Status", "Department", "Percentage", "Obtained Marks", "Total Marks"];
    const rows = reportsToExport.map((row, idx) => [
      idx + 1,
      row.name || "",
      row.employee_id || "",
      row.userAttemptCount ?? row.attempt_count ?? "",
      row.status || "",
      row.department || "",
      row.percentage || 0,
      row.obtained_marks || 0,
      row.total_marks || 0
    ]);

    const csvContent = "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map(r => r.join(","))].join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csvContent);
    link.download = `exam_reports.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
 
  const handleQuestionWiseVideos = async (row) => {
 
    console.log("Clicked");
    setVideoLoading(true);
    try {
      const examTypeId = examType === "Competency Review" ? 1 : 2;
      console.log(
        `/reports/question-wise-videos/${row.ee_guid}/${examTypeId}/`
      );
      const response = await axios.get(
        `/reports/question-wise-videos/${row.ee_guid}/${examTypeId}/`,
        { withCredentials: true }
      );
      const data = response.data;
      if (response.status < 200 || response.status >= 300) {
        alert(data.message || "Failed to fetch videos");
        return;
      }
      setQuestionVideos(data.Questions || []);
      // setSelectedQuestion(null);
      const questions = data.Questions || [];
 
      setQuestionVideos(questions);
      setSelectedQuestion(questions.length ? questions[0] : null);
      setVideoDialogOpen(true);
    } catch (err) {
      console.error(err);
      alert("Network error. Please try again.");
    } finally {
      setVideoLoading(false);
    }
  };
 
 
  const handleSelectReport = (row) => {
    setSelectedReports((prev) => {
      const exists = prev.find(item => item.ee_guid === row.ee_guid);
      return exists ? prev.filter(item => item.ee_guid !== row.ee_guid) : [...prev, row];
    });
  };

  const handleSelectAll = async (event) => {
    if (!event.target.checked) {
      setSelectedReports([]);
      return;
    }

    const allReports = await fetchAllReports();
    setSelectedReports(allReports);
  };

  // ================== UPDATED: Bulk Download (respects selection) ==================
  const handleBulkDownload = async () => {
    try {
      setBulkDownloading(true);

      let reportsToDownload = [];

      if (selectedReports.length > 0) {
        reportsToDownload = selectedReports;
      } else {
        reportsToDownload = await fetchAllReports();
      }

      if (reportsToDownload.length === 0) {
        alert("No reports available to download");
        return;
      }

      const examTypeId = examType === "Competency Review" ? 2 : 1;

      const employees = reportsToDownload.map(row => ({
        ee_id: row.ee_guid,
        name: row.name || "Unknown"
      }));

      const response = await axios.post(
        "/reports/bulk-report/",
        {
          exam_type_id: examTypeId,
          employees
        },
        {
          headers: { "Content-Type": "application/json" },
          responseType: "blob",
          withCredentials: true
        }
      );

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "reports.zip";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error("Bulk download error:", error);
      alert("Failed to download reports");
    } finally {
      setBulkDownloading(false);
    }
  };

  return (
    <>
      <div style={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: "10px",
        marginBottom: "18px",
        flexWrap: "wrap"
      }}>
        {attemptFilter}
        <button
          onClick={() => { fetchPendingReports(1); setPendingDialogOpen(true); }}
          style={{
            border: "2px solid #22c55e", color: "#111", background: "#fff",
            padding: "10px 22px", borderRadius: "10px", fontWeight: 600,
            cursor: "pointer", minWidth: "160px", height: "48px"
          }}
          onMouseEnter={(e) => { e.target.style.background = "#22c55e"; e.target.style.color = "#fff"; }}
          onMouseLeave={(e) => { e.target.style.background = "#fff"; e.target.style.color = "#111"; }}
        >
          Pending Exams
        </button>

        <button
          onClick={handleExportCSV}
          style={{
            display: "flex", alignItems: "center", gap: "8px",
            background: "#fff", border: "2px solid #22c55e", borderRadius: "12px",
            padding: "12px 24px", fontWeight: 600, cursor: "pointer", color: "#111"
          }}
        >
          <DownloadIcon />
          {selectedReports.length > 0 ? `Export Selected (${selectedReports.length})` : "Export CSV"}
        </button>

        <button
          onClick={handleBulkDownload}
          disabled={bulkDownloading}
          style={{
            display: "flex", alignItems: "center", gap: "8px",
            background: bulkDownloading ? "#d1d5db" : "#fff",
            border: "2px solid #22c55e", borderRadius: "12px",
            padding: "12px 24px", fontWeight: 600,
            cursor: bulkDownloading ? "not-allowed" : "pointer",
            color: "#111", opacity: bulkDownloading ? 0.7 : 1
          }}
        >
          <Files size={20} />
          {bulkDownloading
            ? "Downloading..."
            : selectedReports.length > 0
              ? `Download Selected (${selectedReports.length})`
              : "Bulk Download All"}
        </button>
      </div>

      {/* Pending Exams Dialog */}
      <Dialog open={pendingDialogOpen} onClose={() => setPendingDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 600 }}>
          Select Employees
          <IconButton onClick={() => setPendingDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
 
        <DialogContent>
          <input
            type="text"
            placeholder="Search by Name, Status, Department, Assigned To... "
            value={pendingSearch}
            onChange={(e) => {
              setPendingSearch(e.target.value);
              fetchPendingReports(1, e.target.value);
            }}
            style={{ height: "42px", width: "100%", marginBottom: "20px",padding:"10px" }}
          />

          <div style={{ maxHeight: "500px", overflow: "auto" }}>
            <table className="pd-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Department</th>
                  <th>Assigned</th>
                </tr>
              </thead>
              <tbody>
                {pendingReports.length > 0 ? (
                  pendingReports.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.name}</td>
                      <td>
                        <span style={{
                          padding: "6px 16px", borderRadius: "20px", fontWeight: 500,
                          background: row.status === "Pending" ? "#fff3cd" : "#f8d7da",
                          color: row.status === "Pending" ? "#856404" : "#721c24"
                        }}>
                          {row.status}
                        </span>
                      </td>
                      <td>{row.department}</td>
                      <td>{row.assigned}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ textAlign: "center", padding: "40px", color: "#777" }}>
                      No reports found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {pendingTotalPages >= 1 && (
            <Box className="pd-pagination" sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
              <button
                className="btn-negative"
                disabled={pendingPage === 1}
                onClick={() => fetchPendingReports(pendingPage - 1)}
              >
                Previous
              </button>
 
              <div className="pd-page-numbers">
                {Array.from({ length: pendingTotalPages }, (_, i) => i + 1).map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => fetchPendingReports(pageNum)}
                    className={`pg-type-btn ${pendingPage === pageNum ? "active" : ""}`}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>
 
              <button
                className="btn-negative"
                disabled={pendingPage === pendingTotalPages}
                onClick={() => fetchPendingReports(pendingPage + 1)}
              >
                Next
              </button>
            </Box>
          )}
 
        </DialogContent>
      </Dialog>

      {/* Main Reports Table */}
      {loading ? (
        <div className="pd-table-loading">
          <div className="pd-table-loading-animation">
            <img className="pd-table-loading-video" src="/images/breath1.gif" alt="Loading..." />
            <p>Loading reports...</p>
          </div>
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto', maxHeight: '400px', borderRadius: '6px' }}>
 
            <table
              className="pd-table"
              style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse" }}
            >
              <thead>
                <tr>
                  <th style={{ width: "60px", minWidth: "60px", maxWidth: "60px", padding: "8px 2px", textAlign: "left", whiteSpace: "nowrap" }}>
                    <input
                      type="checkbox"
                      checked={allFilteredReportsSelected}
                      ref={(input) => {
                        if (input) {
                          input.indeterminate = selectedReports.length > 0 && !allFilteredReportsSelected;
                        }
                      }}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th style={{ width: "60px", minWidth: "60px", maxWidth: "60px", padding: "8px 4px", textAlign: "left", whiteSpace: "nowrap" }}>S.No</th>
                  <th style={{ width: "130px", minWidth: "130px", maxWidth: "130px", padding: "8px 8px", textAlign: "left", whiteSpace: "nowrap" }}>Name</th>
                  <th style={{ width: "130px", minWidth: "130px", maxWidth: "130px", padding: "8px 8px", textAlign: "left", whiteSpace: "nowrap" }}>Employee ID</th>
                  <th style={{ width: "110px", minWidth: "110px", maxWidth: "110px", padding: "8px 8px", textAlign: "left", whiteSpace: "nowrap" }}>Attempt Count</th>
                  <th style={{ width: "92px", minWidth: "92px", maxWidth: "92px", padding: "8px 8px", textAlign: "left", whiteSpace: "nowrap" }}>Status</th>
                  <th style={{ width: "230px", minWidth: "230px", maxWidth: "230px", padding: "8px 8px", textAlign: "left", whiteSpace: "nowrap" }}>Department</th>
                  <th style={{ width: "90px", minWidth: "90px", maxWidth: "90px", padding: "8px 8px", textAlign: "left", whiteSpace: "nowrap" }}>Percentage</th>
                  <th style={{ width: "96px", minWidth: "96px", maxWidth: "96px", padding: "8px 8px", textAlign: "left", whiteSpace: "nowrap" }}>Total Marks</th>
                  <th style={{ width: "66px", minWidth: "66px", maxWidth: "66px", padding: "8px 6px", textAlign: "center", whiteSpace: "nowrap" }}>Actions</th>
                </tr>
              </thead>
 
              <tbody>
                {filteredReports.length === 0 ? (
                  <TableEmptyRow colSpan={10} message="No reports found" />
                ) : filteredReports.map((row, idx) => (
                  <tr key={idx}>
                    <td style={{ width: "34px", minWidth: "34px", maxWidth: "34px", padding: "8px 2px", textAlign: "center", whiteSpace: "nowrap" }}>
                      <input
                        type="checkbox"
                        style={{ width: "16px", height: "16px", margin: 0, display: "inline-block" }}
                        checked={selectedReports.some(item => item.ee_guid === row.ee_guid)}
                        onChange={() => handleSelectReport(row)}
                      />
                    </td>
                    <td style={{ width: "52px", minWidth: "52px", maxWidth: "52px", padding: "8px 4px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(page - 1) * rowsPerPage + idx + 1}</td>
                    <td style={{ width: "130px", minWidth: "130px", maxWidth: "130px", padding: "8px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.name}</td>
                    <td style={{ width: "130px", minWidth: "130px", maxWidth: "130px", padding: "8px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.employee_id || "-"}</td>
                    <td style={{ width: "110px", minWidth: "110px", maxWidth: "110px", padding: "8px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {row.userAttemptCount != null ? row.userAttemptCount : "-"}
                    </td>
                    <td style={{ width: "92px", minWidth: "92px", maxWidth: "92px", padding: "8px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <span style={{
                        padding: "4px 12px", borderRadius: "20px", fontWeight: 500,
                        background: row.status === "Passed" ? "#d4edda" : "#f8d7da",
                        color: row.status === "Passed" ? "#155724" : "#721c24"
                      }}>
                        {row.status}
                      </span>
                    </td>
                    <td style={{ width: "230px", minWidth: "230px", maxWidth: "230px", padding: "8px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.department}</td>
                    <td style={{ width: "90px", minWidth: "90px", maxWidth: "90px", padding: "8px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {row.percentage != null
                        ? `${Number(row.percentage).toFixed(2)}%`
                        : "-"}
                    </td>
                    <td style={{ width: "96px", minWidth: "96px", maxWidth: "96px", padding: "8px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.obtained_marks}/{row.total_marks}</td>
                    <td style={{ width: "66px", minWidth: "66px", maxWidth: "66px", padding: "8px 6px", textAlign: "center", whiteSpace: "nowrap" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          // justifyContent: "center",
                        }}
                      >
                        <button onClick={() => handleViewReport(row)} className="btn-no-padding" title="View Report">
                          <FaEye className="action-icon" />
                        </button>
 
 
                        {isCompetencyExam && (
                          <button
                            onClick={() => handleQuestionWiseVideos(row)}
                            className="btn-no-padding"
                            title="Question Wise Videos"
                          >
                            🎥
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredReports.length > 0 && <Box className="pd-pagination" sx={{ mt: 2 }}>
            <button className="btn-negative" disabled={page === 1} onClick={() => setPage(page - 1)}>
              Previous
            </button>

            <div className="pd-page-numbers">
              {renderPageNumbers().map((pageNum, index) => (
                <button
                  key={index}
                  disabled={pageNum === "..."}
                  onClick={() => typeof pageNum === "number" && setPage(pageNum)}
                  className={`pg-type-btn ${pageNum === page ? "active" : ""}`}
                >
                  {pageNum}
                </button>
              ))}
            </div>

            <button
              className="btn-negative"
              disabled={page === totalPages || totalCount === 0}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </Box>}
        </>
      )}
 
      {/* ================= NEW VIDEO DIALOG ================= */}
      <Dialog
        open={videoDialogOpen}
        onClose={() => setVideoDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            height: '80vh',
            maxHeight: '700px',
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Question Wise Videos
          <IconButton onClick={() => setVideoDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <div style={{ display: 'flex', height: '100%', minHeight: '400px' }}>
            {/* Left panel – question list */}
            <div
              style={{
                width: '35%',
                borderRight: '1px solid #e0e0e0',
                overflowY: 'auto',
                padding: '16px',
                backgroundColor: '#fafafa'
              }}
            >
              {questionVideos.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#999', marginTop: '40px' }}>
                  No questions available.
                </div>
              ) : (
                questionVideos.map((q, idx) => (
                  <div
                    key={q.QuestionId || idx}
                    onClick={() => setSelectedQuestion(q)}
                    style={{
                      padding: '12px 16px',
                      marginBottom: '8px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: selectedQuestion?.QuestionId === q.QuestionId ? '#1976d2' : 'white',
                      color: selectedQuestion?.QuestionId === q.QuestionId ? 'white' : '#333',
                      boxShadow: selectedQuestion?.QuestionId === q.QuestionId ? '0 2px 8px rgba(25,118,210,0.3)' : 'none',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>▶</span>
                    <span style={{ fontWeight: 500 }}>Question {idx + 1}</span>
                  </div>
                ))
              )}
            </div>
 
            {/* Right panel – video player */}
            <div
              style={{
                width: '65%',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'white'
              }}
            >
              {selectedQuestion ? (
                <>
                  <h3 style={{ marginTop: 0, marginBottom: '16px', width: '100%', textAlign: 'left' }}>
                    {selectedQuestion.QuestionDescription}
                  </h3>
                  <video
                    controls
                    width="100%"
                    src={selectedQuestion.VideoUrl}
                    style={{ borderRadius: '8px', maxHeight: '400px' }}
                    autoPlay={false}
                  />
                </>
              ) : (
                <div style={{ color: '#999', fontSize: '18px' }}>
                  Select a question to play the video
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Table;
 
 
